import React from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Alert, Button, Empty, Popconfirm, Space, Table, Tag, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import { getFlightTimeDisplayInfo } from "../../lib/flightTimingUtils";
import { getTrackFlights, toArray } from "../../lib/trackUtils";

dayjs.extend(utc);

const { Text } = Typography;
export const COMPARE_MATCH_TABLE_PAGE_SIZE = 50;

const STATUS_COLOR = {
    MATCHED: "green",
    INVALID: "red",
    NEW: "blue",
    CANCELLED: "volcano",
    IGNORE: "gold",
    UNMATCHED: "orange",
};

const STATUS_LABEL = {
    MATCHED: "已匹配",
    INVALID: "失效",
    NEW: "新增",
    CANCELLED: "取消",
    IGNORE: "忽略",
    UNMATCHED: "待处理",
};

const PUBLICATION_STATUS_LABEL = {
    MATCHED: "已替换",
    INVALID: "发布快照缺失",
    NEW: "已发布新增",
    CANCELLED: "已发布取消",
    IGNORE: "已发布忽略",
    UNMATCHED: "发布快照缺失",
};

const MATCH_TYPE_LABEL = {
    AUTO: "自动",
    MANUAL: "人工",
};

const ROW_TYPE_LABEL = {
    paired: "配对",
    editing_single: "仅草稿",
    reference_single: "仅执行",
    unresolved: "待处理",
};

const PUBLICATION_ROW_TYPE_LABEL = {
    paired: "发布配对",
    editing_single: "仅发布方案",
    reference_single: "仅基线快照",
    unresolved: "发布事实缺项",
};

const TRANSACTION_TYPE_LABEL = {
    fuel_request: "燃油",
    permit_request: "许可",
    catering_order: "配餐",
    flight_brief: "Brief",
    crew_transportation: "机组交通",
    hotac: "HOTAC",
};

const MIGRATION_STATUS_LABEL = {
    PENDING_REVIEW: "待复核",
    CONFIRMED: "已确认",
    REJECTED: "已拒绝",
};

const EXECUTION_STATUS_LABEL = {
    SCHEDULED: "计划",
    ACTIVE: "执行中",
    DELAYED: "延误",
    DIVERTED: "备降",
    CANCELLED: "已取消",
    COMPLETED: "已完成",
};

function asKey(value) {
    return value == null ? null : String(value);
}

function getReferenceFlightSourceId(flight) {
    return asKey(flight?.source_operation_id ?? flight?.source_flight_id ?? flight?.id);
}

function getMatchSourceId(match) {
    return asKey(match?.source_operation_id ?? match?.source_flight_id);
}

function getMatchDraftFlightId(match) {
    return asKey(match?.schedule_draft_flight_id ?? match?.draft_flight_id);
}

function getMatchStatus(match, { publicationReview = false } = {}) {
    if (publicationReview) {
        return String(match?.match_status || "UNMATCHED").toUpperCase();
    }
    if (match?.is_valid === false) {
        return "INVALID";
    }
    return String(match?.effective_status || match?.match_status || "UNMATCHED").toUpperCase();
}

function getRawMatchStatus(match) {
    return String(match?.match_status || "UNMATCHED").toUpperCase();
}

function getMatchType(match) {
    return match ? String(match?.match_type || "AUTO").toUpperCase() : null;
}

export function getCompareMatchTableStatusLabel(status, { publicationReview = false } = {}) {
    if (publicationReview) {
        return PUBLICATION_STATUS_LABEL[status] || "发布快照缺失";
    }
    return STATUS_LABEL[status] || "状态待确认";
}

function getRowTypeLabel(rowType, { publicationReview = false } = {}) {
    if (publicationReview) {
        return PUBLICATION_ROW_TYPE_LABEL[rowType] || rowType;
    }
    return ROW_TYPE_LABEL[rowType] || "待处理";
}

function getMatchTypeLabel(matchType) {
    return MATCH_TYPE_LABEL[matchType] || "决策待确认";
}

function getExecutionStatusLabel(status) {
    const normalizedStatus = String(status || "").toUpperCase();
    return EXECUTION_STATUS_LABEL[normalizedStatus] || "执行状态待确认";
}

function getDisplayOrder(flight, fallbackIndex) {
    const order = Number(flight?.display_order ?? flight?.displayOrder);
    return Number.isFinite(order) ? order : fallbackIndex;
}

function normalizeTransactionSummaryMap(transactionSummaries) {
    const items = Array.isArray(transactionSummaries)
        ? transactionSummaries
        : toArray(transactionSummaries?.items || transactionSummaries?.summaries || transactionSummaries?.data?.items);
    return new Map(items.map((item) => [asKey(item?.flight_id ?? item?.source_operation_id ?? item?.operation_id), item]).filter(([key]) => key));
}

function addConsumedId(set, value) {
    const key = asKey(value);
    if (key) {
        set.add(key);
    }
}

function getRowType(draftFlightId, sourceId) {
    if (draftFlightId && sourceId) {
        return "paired";
    }
    if (draftFlightId) {
        return "editing_single";
    }
    if (sourceId) {
        return "reference_single";
    }
    return "unresolved";
}

function getRowKey(match, draftFlightId, sourceId, suffix) {
    return asKey(match?.id) || `${draftFlightId || "-"}:${sourceId || "-"}:${suffix}`;
}

function getFlightLabel(flight) {
    if (!flight) {
        return "缺失航班";
    }
    const leg = [flight.departure_airport, flight.arrival_airport].filter(Boolean).join("-");
    return [flight.flight_number || "未命名", leg].filter(Boolean).join(" · ");
}

function formatTime(value) {
    if (!value) {
        return "--";
    }
    const parsed = dayjs.utc(value);
    return parsed.isValid() ? parsed.format("MM-DD HH:mm") : String(value);
}

function getMinutesDiff(left, right) {
    if (!left || !right) {
        return null;
    }
    const leftTime = dayjs.utc(left);
    const rightTime = dayjs.utc(right);
    if (!leftTime.isValid() || !rightTime.isValid()) {
        return null;
    }
    return leftTime.diff(rightTime, "minute");
}

function formatMinutesDiff(value) {
    if (value === null || value === undefined) {
        return null;
    }
    const sign = value > 0 ? "+" : "";
    return `${sign}${value}m`;
}

function isDifferent(left, right) {
    return String(left || "").trim().toUpperCase() !== String(right || "").trim().toUpperCase();
}

function isCrossDate(editingFlight, referenceFlight) {
    const editingDeparture = editingFlight?.std ? dayjs.utc(editingFlight.std) : null;
    const referenceDepartureValue = getFlightTimeDisplayInfo(referenceFlight, { useEffectiveTime: true }).departureTime;
    const referenceDeparture = referenceDepartureValue ? dayjs.utc(referenceDepartureValue) : null;
    return Boolean(editingDeparture?.isValid() && referenceDeparture?.isValid() && !editingDeparture.isSame(referenceDeparture, "day"));
}

function buildDiffItems(editingFlight, referenceFlight) {
    if (!editingFlight || !referenceFlight) {
        return [];
    }
    const editingTimeInfo = getFlightTimeDisplayInfo(editingFlight);
    const referenceTimeInfo = getFlightTimeDisplayInfo(referenceFlight, { useEffectiveTime: true });
    const departureDiff = getMinutesDiff(editingTimeInfo.departureTime, referenceTimeInfo.departureTime);
    const arrivalDiff = getMinutesDiff(editingTimeInfo.arrivalTime, referenceTimeInfo.arrivalTime);
    const items = [];
    if (departureDiff !== null || arrivalDiff !== null) {
        items.push(`时间 ${formatMinutesDiff(departureDiff) || "--"}/${formatMinutesDiff(arrivalDiff) || "--"}`);
    }
    if (isDifferent(editingFlight.aircraft_reg, referenceFlight.aircraft_reg)) {
        items.push("飞机不同");
    }
    if (isDifferent(editingFlight.departure_airport, referenceFlight.departure_airport) || isDifferent(editingFlight.arrival_airport, referenceFlight.arrival_airport)) {
        items.push("航段不同");
    }
    const status = referenceFlight?.source_metadata?.status || referenceFlight?.status;
    if (status && !["SCHEDULED", "ACTIVE", "DELAYED", "DIVERTED"].includes(String(status).toUpperCase())) {
        items.push(`执行状态 ${getExecutionStatusLabel(status)}`);
    }
    if (isCrossDate(editingFlight, referenceFlight)) {
        items.push("跨日期");
    }
    return items;
}

export function buildCompareMatchTableRows({ editingTrack, referenceTrack, matches = [], transactionSummaries = null, publicationReview = false } = {}) {
    const editingFlights = getTrackFlights(editingTrack);
    const referenceFlights = getTrackFlights(referenceTrack);
    const transactionSummaryMap = normalizeTransactionSummaryMap(transactionSummaries);
    const editingFlightById = new Map(editingFlights.map((flight, index) => [asKey(flight?.id), { flight, index }]).filter(([key]) => key));
    const referenceFlightBySourceId = new Map(referenceFlights.map((flight, index) => [getReferenceFlightSourceId(flight), { flight, index }]).filter(([key]) => key));
    const consumedDraftIds = new Set();
    const consumedSourceIds = new Set();
    const rows = [];

    toArray(matches).forEach((match, index) => {
        const draftFlightId = getMatchDraftFlightId(match);
        const sourceId = getMatchSourceId(match);
        const editingEntry = editingFlightById.get(draftFlightId) || null;
        const referenceEntry = referenceFlightBySourceId.get(sourceId) || null;
        const rowType = getRowType(draftFlightId, sourceId);
        addConsumedId(consumedDraftIds, draftFlightId);
        addConsumedId(consumedSourceIds, sourceId);
        rows.push({
            key: getRowKey(match, draftFlightId, sourceId, index),
            rowType,
            status: getMatchStatus(match, { publicationReview }),
            rawStatus: getRawMatchStatus(match),
            matchType: getMatchType(match),
            match,
            editingFlight: editingEntry?.flight || null,
            referenceFlight: referenceEntry?.flight || null,
            draftFlightId,
            sourceOperationId: sourceId,
            transactionSummary: transactionSummaryMap.get(sourceId) || null,
            sortIndex: Math.min(editingEntry ? getDisplayOrder(editingEntry.flight, editingEntry.index) : Number.MAX_SAFE_INTEGER, referenceEntry ? getDisplayOrder(referenceEntry.flight, referenceEntry.index) : Number.MAX_SAFE_INTEGER),
            fallbackIndex: index,
            projected: false,
        });
    });

    editingFlights.forEach((flight, index) => {
        const draftFlightId = asKey(flight?.id);
        if (!draftFlightId || consumedDraftIds.has(draftFlightId)) {
            return;
        }
        rows.push({
            key: `unmatched-editing:${draftFlightId}`,
            rowType: "editing_single",
            status: "UNMATCHED",
            rawStatus: "UNMATCHED",
            matchType: null,
            match: null,
            editingFlight: flight,
            referenceFlight: null,
            draftFlightId,
            sourceOperationId: null,
            transactionSummary: null,
            sortIndex: getDisplayOrder(flight, index),
            fallbackIndex: rows.length,
            projected: true,
        });
    });

    referenceFlights.forEach((flight, index) => {
        const sourceId = getReferenceFlightSourceId(flight);
        if (!sourceId || consumedSourceIds.has(sourceId)) {
            return;
        }
        rows.push({
            key: `unmatched-reference:${sourceId}`,
            rowType: "reference_single",
            status: "UNMATCHED",
            rawStatus: "UNMATCHED",
            matchType: null,
            match: null,
            editingFlight: null,
            referenceFlight: flight,
            draftFlightId: null,
            sourceOperationId: sourceId,
            transactionSummary: transactionSummaryMap.get(sourceId) || null,
            sortIndex: getDisplayOrder(flight, index),
            fallbackIndex: rows.length,
            projected: true,
        });
    });

    return rows.sort((left, right) => {
        if (left.sortIndex !== right.sortIndex) {
            return left.sortIndex - right.sortIndex;
        }
        return left.fallbackIndex - right.fallbackIndex;
    });
}

const FlightCell = React.memo(function FlightCell({ flight, role }) {
    if (!flight) {
        return <Text type="secondary">--</Text>;
    }
    const timeInfo = getFlightTimeDisplayInfo(flight, { useEffectiveTime: role === "reference" });
    const status = role === "reference" ? flight?.source_metadata?.status || flight?.status : null;
    return (
        <Space direction="vertical" size={2} className="schedule-draft-match-table-flight-cell">
            <Text strong>{getFlightLabel(flight)}</Text>
            <Text type="secondary">{flight.aircraft_reg || "--"}</Text>
            <Text type="secondary">
                {timeInfo.departureSourceLabel} {formatTime(timeInfo.departureTime)} / {timeInfo.arrivalSourceLabel} {formatTime(timeInfo.arrivalTime)}
            </Text>
            {status ? <Tag>{getExecutionStatusLabel(status)}</Tag> : null}
        </Space>
    );
});

const DiffCell = React.memo(function DiffCell({ editingFlight, referenceFlight, status, publicationReview }) {
    const items = buildDiffItems(editingFlight, referenceFlight);
    if (!publicationReview && status === "INVALID") {
        items.unshift("匹配已失效");
    }
    if (!items.length) {
        return <Text type="secondary">无配对差异</Text>;
    }
    return (
        <Space wrap size={[4, 4]}>
            {items.map((item) => <Tag key={item} color={item.includes("失效") || item.includes("状态") ? "red" : "default"}>{item}</Tag>)}
        </Space>
    );
});

const TransactionSummaryCell = React.memo(function TransactionSummaryCell({ row, loading, error }) {
    if (!row.sourceOperationId) {
        return <Text type="secondary">无执行航班</Text>;
    }
    if (loading) {
        return <Text type="secondary">读取中</Text>;
    }
    if (error) {
        return <Text type="secondary">暂不可用</Text>;
    }
    const summary = row.transactionSummary;
    if (!summary || !summary.total) {
        return <Text type="secondary">无当前事务</Text>;
    }
    const byType = summary.by_type || {};
    const byMigrationStatus = summary.by_migration_status || {};
    return (
        <Space direction="vertical" size={2}>
            <Space wrap size={[4, 4]}>
                <Tag color="orange">事务 {summary.total}</Tag>
                {Object.entries(byType).filter(([, count]) => count > 0).map(([type, count]) => (
                    <Tag key={type}>{TRANSACTION_TYPE_LABEL[type] || type} {count}</Tag>
                ))}
                {Object.entries(byMigrationStatus).filter(([status, count]) => status !== "NONE" && count > 0).map(([status, count]) => (
                    <Tag key={status} color={status === "PENDING_REVIEW" ? "gold" : undefined}>{MIGRATION_STATUS_LABEL[status] || "其他状态"} {count}</Tag>
                ))}
            </Space>
        </Space>
    );
});

const RowActions = React.memo(function RowActions({ row, readOnly, onMatchDelete, onMarkNew, onMarkCancelled, onMarkIgnore }) {
    if (readOnly) {
        return <Text type="secondary">只读</Text>;
    }
    const canDelete = Boolean(row.match);
    const canMarkEditing = Boolean(row.editingFlight);
    const canMarkReference = Boolean(row.referenceFlight);
    return (
        <Space wrap size={[4, 4]}>
            {canDelete ? (
                <Popconfirm title="解除这条匹配？" okText="解除" cancelText="取消" onConfirm={() => onMatchDelete?.(row.match)}>
                    <Button size="small">解除匹配</Button>
                </Popconfirm>
            ) : null}
            {canMarkEditing ? (
                <Button size="small" disabled={row.status === "NEW"} onClick={() => onMarkNew?.(row.editingFlight)}>标为新增</Button>
            ) : null}
            {canMarkReference ? (
                <Button size="small" disabled={row.status === "CANCELLED"} onClick={() => onMarkCancelled?.(row.referenceFlight)}>标为取消</Button>
            ) : null}
            {canMarkEditing ? (
                <Button size="small" disabled={row.status === "IGNORE" && row.rowType === "editing_single"} onClick={() => onMarkIgnore?.("editing", row.editingFlight)}>忽略草稿航班</Button>
            ) : null}
            {canMarkReference ? (
                <Button size="small" disabled={row.status === "IGNORE" && row.rowType === "reference_single"} onClick={() => onMarkIgnore?.("reference", row.referenceFlight)}>忽略执行航班</Button>
            ) : null}
        </Space>
    );
});

const columns = ({ readOnly, publicationReview, transactionSummaryLoading, transactionSummaryError, onMatchDelete, onMarkNew, onMarkCancelled, onMarkIgnore }) => [
    {
        title: "决策",
        dataIndex: "status",
        width: 190,
        filters: ["UNMATCHED", "INVALID", "NEW", "CANCELLED", "IGNORE", "MATCHED"].map((status) => ({ text: getCompareMatchTableStatusLabel(status, { publicationReview }), value: status })),
        onFilter: (value, row) => row.status === value,
        render: (_, row) => (
            <Space direction="vertical" size={4}>
                <Space wrap size={[4, 4]}>
                    <Tag color={STATUS_COLOR[row.status] || "default"}>{getCompareMatchTableStatusLabel(row.status, { publicationReview })}</Tag>
                    {row.matchType ? <Tag>{getMatchTypeLabel(row.matchType)}</Tag> : null}
                </Space>
                <Text type="secondary">{getRowTypeLabel(row.rowType, { publicationReview })}</Text>
            </Space>
        ),
    },
    {
        title: "草稿航班",
        dataIndex: "editingFlight",
        width: 240,
        render: (flight) => <FlightCell flight={flight} role="editing" />,
    },
    {
        title: "执行航班",
        dataIndex: "referenceFlight",
        width: 260,
        render: (flight) => <FlightCell flight={flight} role="reference" />,
    },
    {
        title: "差异",
        dataIndex: "diff",
        width: 220,
        render: (_, row) => <DiffCell editingFlight={row.editingFlight} referenceFlight={row.referenceFlight} status={row.status} publicationReview={publicationReview} />,
    },
    {
        title: "事务",
        dataIndex: "transactionSummary",
        width: 240,
        render: (_, row) => <TransactionSummaryCell row={row} loading={transactionSummaryLoading} error={transactionSummaryError} />,
    },
    {
        title: "操作",
        dataIndex: "actions",
        fixed: "right",
        width: 260,
        render: (_, row) => (
            <RowActions
                row={row}
                readOnly={readOnly}
                onMatchDelete={onMatchDelete}
                onMarkNew={onMarkNew}
                onMarkCancelled={onMarkCancelled}
                onMarkIgnore={onMarkIgnore}
            />
        ),
    },
];

const CompareMatchTable = React.memo(function CompareMatchTable({
    editingTrack,
    referenceTrack,
    matches = [],
    pairDirty = false,
    readOnly = false,
    publicationReview = false,
    transactionSummaries = null,
    transactionSummaryLoading = false,
    transactionSummaryError = null,
    onMatchDelete,
    onMarkNew,
    onMarkCancelled,
    onMarkIgnore,
}) {
    const rows = React.useMemo(
        () => buildCompareMatchTableRows({ editingTrack, referenceTrack, matches, transactionSummaries, publicationReview }),
        [editingTrack, matches, publicationReview, referenceTrack, transactionSummaries],
    );
    const tableColumns = React.useMemo(
        () => columns({ readOnly, publicationReview, transactionSummaryLoading, transactionSummaryError, onMatchDelete, onMarkNew, onMarkCancelled, onMarkIgnore }),
        [onMarkCancelled, onMarkIgnore, onMarkNew, onMatchDelete, publicationReview, readOnly, transactionSummaryError, transactionSummaryLoading],
    );
    const tablePagination = rows.length > COMPARE_MATCH_TABLE_PAGE_SIZE
        ? { pageSize: COMPARE_MATCH_TABLE_PAGE_SIZE, showSizeChanger: false, hideOnSinglePage: true }
        : false;

    if (!editingTrack || !referenceTrack) {
        return null;
    }

    return (
        <div className="schedule-draft-match-table-section">
            <div className="schedule-draft-match-table-header">
                <Space wrap size={[6, 6]} align="center">
                    <Text strong>决策表</Text>
                    <Tag>{rows.length} 条决策</Tag>
                    {readOnly ? <Tag color="green">只读回看</Tag> : pairDirty ? <Tag color="orange">本地未保存</Tag> : <Tag>已保存</Tag>}
                    {publicationReview ? <Tag color="cyan">发布快照</Tag> : null}
                </Space>
                <Tooltip title="表格与上方连线共用同一份本地匹配工作态。">
                    <Button type="text" size="small" icon={<InfoCircleOutlined />} aria-label="匹配决策说明" />
                </Tooltip>
            </div>
            {transactionSummaryError ? <Alert type="warning" showIcon className="schedule-draft-match-table-note" message="事务摘要读取失败，可稍后重试" /> : null}
            <Table
                size="small"
                rowKey="key"
                columns={tableColumns}
                dataSource={rows}
                pagination={tablePagination}
                scroll={{ x: 1420, y: 520 }}
                locale={{ emptyText: <Empty description="暂无匹配审查项" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
        </div>
    );
});

export default CompareMatchTable;
